import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, KeyRound, ShieldOff, ShieldCheck, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const API = process.env.REACT_APP_BACKEND_URL;

const authConfig = () => {
  const token = localStorage.getItem('njk_admin_token');
  return {
    withCredentials: true,
    headers: token && token !== 'google-session' ? { Authorization: `Bearer ${token}` } : {},
  };
};

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [allowlist, setAllowlist] = useState('');
  const [savingAllowlist, setSavingAllowlist] = useState(false);

  const fetchAllowlist = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/google-allowlist`, authConfig());
      setAllowlist((res.data.emails || []).join(', '));
    } catch (e) { /* non-blocking */ }
  };

  const handleSaveAllowlist = async () => {
    setSavingAllowlist(true);
    try {
      const emails = allowlist.split(',').map((e) => e.trim()).filter(Boolean);
      const res = await axios.put(`${API}/api/settings/google-allowlist`, { emails }, authConfig());
      setAllowlist((res.data.emails || []).join(', '));
      toast.success(res.data.emails.length > 0
        ? `Google sign-in restricted to ${res.data.emails.length} email(s)`
        : 'Allowlist cleared — ANY Google account can sign in to admin', { duration: 8000 });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save allowlist'));
    } finally {
      setSavingAllowlist(false);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/auth/staff`, authConfig());
      setStaff(res.data.staff || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load staff accounts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); fetchAllowlist(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Please fill in name, email and password');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/staff`, form, authConfig());
      toast.success(`Staff account created for ${form.name}`);
      setShowCreate(false);
      setForm({ name: '', email: '', password: '' });
      fetchStaff();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create staff account'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/auth/staff/${resetTarget.user_id}/password`, { password: newPassword }, authConfig());
      toast.success(`Password updated for ${resetTarget.name}`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to reset password'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (s) => {
    try {
      const res = await axios.put(`${API}/api/auth/staff/${s.user_id}/active`, {}, authConfig());
      toast.success(res.data.active ? `${s.name} re-activated` : `${s.name} deactivated — they can no longer log in`);
      fetchStaff();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update account'));
    }
  };

  const handleToggleBilling = async (s) => {
    try {
      const res = await axios.put(`${API}/api/auth/staff/${s.user_id}/billing`, {}, authConfig());
      toast.success(res.data.full_billing
        ? `${s.name} now has full billing access`
        : `${s.name} limited to Invoices & Delivery Orders only`);
      fetchStaff();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update billing access'));
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete staff account for ${s.name} (${s.email})? They will no longer be able to log in.`)) return;
    try {
      await axios.delete(`${API}/api/auth/staff/${s.user_id}`, authConfig());
      toast.success('Staff account deleted');
      fetchStaff();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete staff account'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40" data-testid="staff-management-page">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Users className="text-[#5c1a1a]" size={30} /> Staff Accounts
            </h1>
            <p className="text-gray-600 mt-1">Create email & password logins for your staff. Staff cannot see Profit Tracker, HR/Payslips, My Notes, Equipment/Lorry servicing or Admin Tools.</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#5c1a1a] hover:bg-[#7a2525]" data-testid="add-staff-btn">
            <Plus size={18} className="mr-2" /> Add Staff
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Staff with login access ({staff.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : staff.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="no-staff-message">
                No staff accounts yet. Click "Add Staff" to create the first one.
              </div>
            ) : (
              <div className="divide-y">
                {staff.map((s) => (
                  <div key={s.user_id} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`staff-row-${s.email}`}>
                    <div>
                      <p className="font-semibold text-gray-900">{s.name}</p>
                      <p className="text-sm text-gray-500">{s.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={s.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}>
                        {s.active ? 'Active' : 'Deactivated'}
                      </Badge>
                      <Badge className={s.full_billing !== false ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'} data-testid={`billing-badge-${s.email}`}>
                        {s.full_billing !== false ? 'Full Billing' : 'Billing Only'}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => handleToggleBilling(s)} data-testid={`toggle-billing-${s.email}`}>
                        <FileSpreadsheet size={14} className="mr-1" /> {s.full_billing !== false ? 'Limit to Billing Only' : 'Grant Full Billing'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setResetTarget(s); setNewPassword(''); }} data-testid={`reset-password-${s.email}`}>
                        <KeyRound size={14} className="mr-1" /> Reset Password
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleActive(s)} data-testid={`toggle-active-${s.email}`}>
                        {s.active ? <><ShieldOff size={14} className="mr-1" /> Deactivate</> : <><ShieldCheck size={14} className="mr-1" /> Activate</>}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(s)} data-testid={`delete-staff-${s.email}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Google Sign-in Allowlist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-600">
              Only these Google emails can sign in with the Google button. <b>Leave empty to allow any Google account</b> (not recommended — your billing data would be open to anyone with a Gmail).
            </p>
            <div>
              <Label>Allowed emails (comma-separated)</Label>
              <Input
                value={allowlist}
                onChange={(e) => setAllowlist(e.target.value)}
                placeholder="you@gmail.com, partner@gmail.com"
                data-testid="google-allowlist-input"
              />
            </div>
            <Button onClick={handleSaveAllowlist} disabled={savingAllowlist} className="bg-[#5c1a1a] hover:bg-[#7a2525]" data-testid="google-allowlist-save-btn">
              {savingAllowlist ? 'Saving...' : 'Save Allowlist'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Account</DialogTitle>
            <DialogDescription>They will log in with this email and password on the admin login page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Afif Rahman" data-testid="staff-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@example.com" data-testid="staff-email-input" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" data-testid="staff-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#5c1a1a] hover:bg-[#7a2525]" data-testid="create-staff-submit">
              {saving ? 'Creating...' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle>
            <DialogDescription>Set a new login password for this staff member.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>New Password</Label>
            <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={saving || newPassword.length < 6} className="bg-[#5c1a1a] hover:bg-[#7a2525]" data-testid="reset-password-submit">
              {saving ? 'Saving...' : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
